import type { DirectionMaxRule, EntityTypeUnion, PositionSizeRule } from '@einhaus/project314-types';

export const positionSizeTypeLabels: Record<PositionSizeRule['type'], string> = {
    fixed: 'Fixed position size',
    percentage: 'Percentage of portfolio',
    minimumDollar: 'Minimum dollar value',
    maximumDollar: 'Maximum dollar value',
    maximumRiskCapitalDollar: 'Maximum risk capital',
    minimumRiskCapitalDollar: 'Minimum risk capital',
    fixedRiskCapitalDollar: 'Fixed risk capital',
    percentagePortfolioRiskCapital: 'Portfolio risk capital percentage'
};

export const directionExposureLabels: Record<DirectionMaxRule['direction'], string> = {
    long: 'Bullish',
    short: 'Bearish',
    either: 'Either exposure'
};

export const directionMaxTypeLabels: Record<DirectionMaxRule['type'], string> = {
    positions: 'positions',
    percentage: 'portfolio',
    value: 'value',
    positionCountGap: 'position gap'
};

const entityTypeLabels: Record<EntityTypeUnion['entityType'] | 'All', string> = {
    All: 'All entity types',
    Asset: 'Stock / option underlying',
    Cryptocurrency: 'Cryptocurrency',
    AssetIndex: 'Index'
};

const percentagePositionSizeTypes = new Set<PositionSizeRule['type']>(['percentage', 'percentagePortfolioRiskCapital']);

export const formatRuleValue = (value: number | string): string => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toLocaleString() : String(value);
};

export const summarizeDirectionRule = (rule: DirectionMaxRule): string => {
    const direction = directionExposureLabels[rule.direction].toLowerCase();
    const suffix = rule.type === 'percentage' ? '%' : rule.type === 'value' ? ' dollars' : '';
    return `${direction} max ${formatRuleValue(rule.value)}${suffix} ${directionMaxTypeLabels[rule.type]}`;
};

export const summarizePositionSizeRule = (rule: PositionSizeRule): string => {
    const isPercentage = percentagePositionSizeTypes.has(rule.type);
    return `${positionSizeTypeLabels[rule.type]} ${isPercentage ? '' : '$'}${formatRuleValue(rule.value)}${isPercentage ? '%' : ''}`;
};

export const describeDirectionRule = (rule: DirectionMaxRule): string => {
    const direction = directionExposureLabels[rule.direction];
    const value = formatRuleValue(rule.value);

    switch (rule.type) {
        case 'positions':
            return `${direction}: ${value} positions`;
        case 'percentage':
            return `${direction}: ${value}% of portfolio`;
        case 'value':
            return `${direction}: $${value} maximum value`;
        case 'positionCountGap':
            return `${direction}: ${value} position count gap`;
    }
};

export const describePositionSizeRule = (rule: PositionSizeRule): string => {
    const isPercentage = percentagePositionSizeTypes.has(rule.type);
    const value = `${isPercentage ? '' : '$'}${formatRuleValue(rule.value)}${isPercentage ? '%' : ''}`;
    const entityTypes = rule.entityTypes.map((entityType) => entityTypeLabels[entityType]).join(', ');

    return `${positionSizeTypeLabels[rule.type]}: ${value} · ${entityTypes}`;
};

const summarizeRules = <T>(rules: readonly T[], formatRule: (rule: T) => string): string => {
    const visibleRules = rules.slice(0, 2).map(formatRule).join(', ');
    const hiddenRuleCount = rules.length - 2;

    return hiddenRuleCount > 0 ? `${visibleRules} +${hiddenRuleCount.toLocaleString()} more` : visibleRules;
};

export const summarizeDirectionRules = (rules: readonly DirectionMaxRule[]): string =>
    rules.length > 0 ? summarizeRules(rules, describeDirectionRule) : 'No position limit';

export const summarizePositionSizeRules = (rules: readonly PositionSizeRule[]): string =>
    rules.length > 0 ? summarizeRules(rules, summarizePositionSizeRule) : 'No default sizing';
